import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format, parseISO } from 'date-fns';
import { SessionDetails, Squad } from '../types';

interface GeneratePDFOptions {
  sessions: SessionDetails[];
  startDate: string;
  endDate: string;
  selectedSquads: Squad[];
  reportType: 'weekly' | 'monthly';
  separateReports: boolean;
}

// Add logo to PDF
const addLogoToPDF = (doc: jsPDF) => {
  const logoUrl = 'https://gcajrvfuujmqkipzkvlw.supabase.co/storage/v1/object/public/mbfc-data//MBFC%20Logo.png';
  const logoSize = 20;
  doc.addImage(logoUrl, 'PNG', doc.internal.pageSize.width - 30, 10, logoSize, logoSize);
};

const generatePlanningReportPDF = ({
  sessions,
  startDate,
  endDate,
  selectedSquads,
  reportType,
  separateReports
}: GeneratePDFOptions): void => {
  if (separateReports) {
    // Generate separate reports for each squad
    selectedSquads.forEach(squad => {
      generateSquadReport(sessions, startDate, endDate, squad, reportType);
    });
  } else {
    // Generate a combined report
    generateCombinedReport(sessions, startDate, endDate, selectedSquads, reportType);
  }
};

const generateSquadReport = (
  sessions: SessionDetails[],
  startDate: string,
  endDate: string,
  squad: Squad,
  reportType: 'weekly' | 'monthly'
): void => {
  // Create new PDF document
  const doc = new jsPDF();
  
  // Add logo
  addLogoToPDF(doc);
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(0, 40, 85); // Navy blue
  doc.text('Maverick Blues FC', 105, 15, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(`${squad} Squad - ${reportType === 'weekly' ? 'Weekly' : 'Monthly'} Plan`, 105, 25, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'yyyy-MM-dd')}`, 105, 35, { align: 'center' });
  
  // Filter sessions for this squad
  const squadSessions = sessions.filter(session => session.squads.includes(squad));
  
  if (squadSessions.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.text(`No sessions found for ${squad} squad in the selected date range.`, 105, 50, { align: 'center' });
  } else {
    // Create table data
    const tableData = squadSessions.map(session => [
      format(parseISO(session.date), 'EEE, MMM d'),
      session.time,
      session.location,
      session.groundBooking,
      session.focus[squad] || 'Not specified'
    ]);
    
    // Add table
    // @ts-ignore - jspdf-autotable types
    doc.autoTable({
      startY: 45,
      head: [['Date', 'Time', 'Location', 'Ground', 'Focus Area']],
      body: tableData,
      headStyles: { fillColor: [0, 40, 85], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 10 }
    });
  }
  
  // Save the PDF
  doc.save(`MBFC_${squad}_${reportType}_plan_${format(new Date(), 'yyyyMMdd')}.pdf`);
};

const generateCombinedReport = (
  sessions: SessionDetails[],
  startDate: string,
  endDate: string,
  selectedSquads: Squad[],
  reportType: 'weekly' | 'monthly'
): void => {
  // Create new PDF document
  const doc = new jsPDF();
  
  // Add logo
  addLogoToPDF(doc);
  
  // Add header
  doc.setFontSize(20);
  doc.setTextColor(0, 40, 85); // Navy blue
  doc.text('Maverick Blues FC', 105, 15, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(`${reportType === 'weekly' ? 'Weekly' : 'Monthly'} Session Planning Report`, 105, 25, { align: 'center' });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date Range: ${format(new Date(startDate), 'MMM d, yyyy')} - ${format(new Date(endDate), 'yyyy-MM-dd')}`, 105, 35, { align: 'center' });
  doc.text(`Squads: ${selectedSquads.join(', ')}`, 105, 42, { align: 'center' });
  
  if (sessions.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.text('No sessions found for the selected criteria.', 105, 50, { align: 'center' });
  } else {
    let yPosition = 50;
    
    // Generate section for each squad
    selectedSquads.forEach((squad, index) => {
      const squadSessions = sessions.filter(session => session.squads.includes(squad));
      
      if (squadSessions.length === 0) return;
      
      // Add squad header
      doc.setFontSize(14);
      doc.setTextColor(0, 40, 85);
      doc.text(`${squad} Squad Plan`, 14, yPosition);
      
      yPosition += 10;
      
      // Create table data
      const tableData = squadSessions.map(session => [
        format(parseISO(session.date), 'EEE, MMM d'),
        session.time,
        session.location,
        session.groundBooking,
        session.focus[squad] || 'Not specified'
      ]);
      
      // Add table
      // @ts-ignore - jspdf-autotable types
      doc.autoTable({
        startY: yPosition,
        head: [['Date', 'Time', 'Location', 'Ground', 'Focus Area']],
        body: tableData,
        headStyles: { fillColor: [0, 40, 85], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 10 }
      });
      
      // @ts-ignore - jspdf-autotable types
      yPosition = doc.lastAutoTable.finalY + 20;
      
      // Add new page if needed
      if (yPosition > 250 && index < selectedSquads.length - 1) {
        doc.addPage();
        yPosition = 20;
        // Add logo to new page
        addLogoToPDF(doc);
      }
    });
  }
  
  // Save the PDF
  doc.save(`MBFC_${reportType}_plan_${format(new Date(), 'yyyyMMdd')}.pdf`);
};

export default generatePlanningReportPDF;